import React, { useState, useEffect } from 'react'
import { Label } from '../ui/label'
import { Input } from '../ui/input'

const DAYS_OF_WEEK = [
  { value: 'MO', label: 'Monday' },
  { value: 'TU', label: 'Tuesday' },
  { value: 'WE', label: 'Wednesday' },
  { value: 'TH', label: 'Thursday' },
  { value: 'FR', label: 'Friday' },
  { value: 'SA', label: 'Saturday' },
  { value: 'SU', label: 'Sunday' },
]

export default function RecurrenceRuleInput({ value, onChange }) {
  const [frequency, setFrequency] = useState('none')
  const [dayOfWeek, setDayOfWeek] = useState('MO')
  const [dayOfMonth, setDayOfMonth] = useState('1')
  const [endCondition, setEndCondition] = useState('never')
  const [occurrenceCount, setOccurrenceCount] = useState('10')
  const [untilDate, setUntilDate] = useState('')

  // Parse initial RRULE value
  useEffect(() => {
    if (!value) {
      setFrequency('none')
      return
    }
    try {
      const parts = value.replace('RRULE:', '').split(';')
      const map = {}
      parts.forEach((p) => {
        const [k, v] = p.split('=')
        if (k && v) map[k] = v
      })

      if (map.FREQ === 'DAILY') setFrequency('daily')
      else if (map.FREQ === 'WEEKLY') {
        setFrequency('weekly')
        if (map.BYDAY) setDayOfWeek(map.BYDAY)
      } else if (map.FREQ === 'MONTHLY') {
        setFrequency('monthly')
        if (map.BYMONTHDAY) setDayOfMonth(map.BYMONTHDAY)
      }

      if (map.COUNT) {
        setEndCondition('count')
        setOccurrenceCount(map.COUNT)
      } else if (map.UNTIL) {
        setEndCondition('until')
        // UNTIL format: 20260101T000000Z -> 2026-01-01
        const u = map.UNTIL.replace('Z', '')
        const y = u.substring(0, 4)
        const m = u.substring(4, 6)
        const d = u.substring(6, 8)
        setUntilDate(`${y}-${m}-${d}`)
      } else {
        setEndCondition('never')
      }
    } catch {
      // ignore parse errors
    }
    // Only run on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Build RRULE string whenever state changes
  useEffect(() => {
    if (frequency === 'none') {
      onChange('')
      return
    }

    let rule = `FREQ=${frequency.toUpperCase()}`

    if (frequency === 'weekly') {
      rule += `;BYDAY=${dayOfWeek}`
    } else if (frequency === 'monthly') {
      rule += `;BYMONTHDAY=${dayOfMonth}`
    }

    if (endCondition === 'count' && occurrenceCount) {
      rule += `;COUNT=${occurrenceCount}`
    } else if (endCondition === 'until' && untilDate) {
      const formatted = untilDate.replace(/-/g, '') + 'T000000Z'
      rule += `;UNTIL=${formatted}`
    }

    onChange(`RRULE:${rule}`)
  }, [frequency, dayOfWeek, dayOfMonth, endCondition, occurrenceCount, untilDate, onChange])

  return (
    <div className="space-y-3">
      <div>
        <Label>Recurrence</Label>
        <select
          value={frequency}
          onChange={(e) => setFrequency(e.target.value)}
          className="mt-1 w-full rounded border border-surface-border bg-surface-linen px-3 py-2 text-sm text-charcoal focus:outline-none focus:ring-2 focus:ring-gold"
        >
          <option value="none">None (one-time)</option>
          <option value="daily">Daily</option>
          <option value="weekly">Weekly</option>
          <option value="monthly">Monthly</option>
        </select>
      </div>

      {frequency === 'weekly' && (
        <div>
          <Label>Day of Week</Label>
          <select
            value={dayOfWeek}
            onChange={(e) => setDayOfWeek(e.target.value)}
            className="mt-1 w-full rounded border border-surface-border bg-surface-linen px-3 py-2 text-sm text-charcoal focus:outline-none focus:ring-2 focus:ring-gold"
          >
            {DAYS_OF_WEEK.map((d) => (
              <option key={d.value} value={d.value}>
                {d.label}
              </option>
            ))}
          </select>
        </div>
      )}

      {frequency === 'monthly' && (
        <div>
          <Label>Day of Month</Label>
          <Input
            type="number"
            min="1"
            max="31"
            value={dayOfMonth}
            onChange={(e) => setDayOfMonth(e.target.value)}
            className="mt-1 bg-surface-linen border-surface-border text-charcoal"
          />
        </div>
      )}

      {frequency !== 'none' && (
        <div>
          <Label>End Condition</Label>
          <select
            value={endCondition}
            onChange={(e) => setEndCondition(e.target.value)}
            className="mt-1 w-full rounded border border-surface-border bg-surface-linen px-3 py-2 text-sm text-charcoal focus:outline-none focus:ring-2 focus:ring-gold"
          >
            <option value="never">Never</option>
            <option value="count">After N occurrences</option>
            <option value="until">Until date</option>
          </select>

          {endCondition === 'count' && (
            <Input
              type="number"
              min="1"
              max="365"
              value={occurrenceCount}
              onChange={(e) => setOccurrenceCount(e.target.value)}
              placeholder="Number of occurrences"
              className="mt-2 bg-surface-linen border-surface-border text-charcoal"
            />
          )}

          {endCondition === 'until' && (
            <Input
              type="date"
              value={untilDate}
              onChange={(e) => setUntilDate(e.target.value)}
              className="mt-2 bg-surface-linen border-surface-border text-charcoal"
            />
          )}
        </div>
      )}
    </div>
  )
}
